// Image helper utility to handle fallback images and prevent infinite loading loops

export class ImageHelper {
  // Generate a placeholder image using a solid color or pattern
  static generatePlaceholder(width = 400, height = 250, text = 'Blog Image', backgroundColor = '#f8f9fa') {
    // Create a simple SVG placeholder
    const svg = `
      <svg width="${width}" height="${height}" xmlns="http://www.w3.org/2000/svg">
        <rect width="100%" height="100%" fill="${backgroundColor}"/>
        <rect x="10" y="10" width="${width-20}" height="${height-20}" fill="none" stroke="#dee2e6" stroke-width="2" stroke-dasharray="5,5"/>
        <text x="50%" y="50%" font-family="Arial, sans-serif" font-size="16" fill="#6c757d" text-anchor="middle" dy=".3em">
          ${text}
        </text>
      </svg>
    `.trim();
    
    return `data:image/svg+xml;base64,${btoa(svg)}`;
  }

  // Get category-specific placeholder color
  static getCategoryPlaceholder(category, width = 400, height = 250) {
    const categoryColors = {
      'Technology': '#667eea',
      'Business': '#f093fb', 
      'Crypto': '#4ecdc4',
      'Startup': '#ff6b6b',
      'Tutorial': '#45b7d1',
      'Reviews': '#96ceb4'
    };
    
    const color = categoryColors[category] || '#6c757d';
    return this.generatePlaceholder(width, height, category, color);
  }

  // Handle image error with fallback logic
  static handleImageError(event, fallbackOptions = {}) {
    const img = event.target;
    
    // Prevent infinite loop by checking if already tried fallback
    if (img.dataset.fallbackAttempted === 'true') {
      console.warn('Image fallback already attempted, using placeholder');
      // Use inline placeholder as final fallback
      img.src = this.generatePlaceholder(
        fallbackOptions.width || 400,
        fallbackOptions.height || 250,
        fallbackOptions.text || 'Image not found',
        fallbackOptions.color || '#f8f9fa'
      );
      return;
    }
    
    // Mark that we've attempted fallback
    img.dataset.fallbackAttempted = 'true';
    
    // Try the provided fallback URL or generate placeholder
    if (fallbackOptions.fallbackUrl) {
      img.src = fallbackOptions.fallbackUrl;
    } else {
      img.src = this.generatePlaceholder(
        fallbackOptions.width || 400,
        fallbackOptions.height || 250,
        fallbackOptions.text || 'Blog Image',
        fallbackOptions.color || '#f8f9fa'
      );
    }
  }

  // Create a React-friendly image error handler
  static createImageErrorHandler(options = {}) {
    return (event) => {
      this.handleImageError(event, options);
    };
  }

  // Check if image exists (useful for validation)
  static async imageExists(url) {
    return new Promise((resolve) => {
      const img = new Image();
      img.onload = () => resolve(true);
      img.onerror = () => resolve(false);
      img.src = url;
    });
  }

  // Preload images to reduce loading errors
  static preloadImages(urls) {
    return Promise.all(
      urls.map(url => 
        new Promise((resolve) => {
          const img = new Image();
          img.onload = () => resolve({ url, success: true });
          img.onerror = () => resolve({ url, success: false });
          img.src = url;
        })
      )
    );
  }
}

export default ImageHelper;